import { Component, OnInit, HostListener, ViewChild, ElementRef, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { interval, Subscription } from 'rxjs';
declare var google: any;
import * as moment from 'moment-timezone';
import { SharedDataService } from 'src/app/service/shared-service/shared-data.service';
import { takeUntil, finalize } from 'rxjs/operators';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import { RouteService } from '../service/route.service';
import { DriversService } from 'src/app/component/drivers/drivers-service/drivers-service';
import { FleetService } from 'src/app/component/fleet/fleet-service/fleet.service';
import { SocketService } from 'src/app/service/socket-service/socket-service.service';
import { AssigTripsService } from '../../assigntrips-service/assigntrips-service';

interface MapStop {
  lat: number;
  lng: number;
  label: string;
  type: string;
  iconUrl?: any;
  address?: string;
  memberName?: string;
  scheduledTime?: string;
  eta?: string;
  delayMinutes?: number;
  actualArrival?: string;
  actualOnboardCount?: number;
  actualOffboardCount?: number;
  actualDeparture?: string;
  plannedOnboard?: any[];
  plannedOffboard?: any[];
  occupancyAfter?: number;
  status?: string;
  showTooltip?: boolean;
  reserveCount?: number;
  distanceFromPrev?: number;
  durationFromPrev?: number;
}

interface TimelineEvent {
  startTime: string; // HH:mm
  endTime: string;   // HH:mm
  label: string;
  memberName?: string;
  type: 'home' | 'stop' | 'vehicle' | 'end';
  actualOnboard?: number;
  actualOffboard?: number;
  actualArrival?: string;
  actualOnboardCount?: number;
  actualOffboardCount?: number;
  actualDeparture?: string;
  plannedOnboard?: number;
  plannedOffboard?: number;
  status?: string;
  occupancyAfter?: number;
  reserveCount?: number;
  distanceFromPrev?: number;
  durationFromPrev?: number;
  eta?: string;
  delayMinutes?: number;
  leftPct?: number;
  isLate?: boolean;
  lateWidthPct?: number;
  showTooltip?: boolean;
}

interface Route {
  id: string;
  _id: string; // compatibility
  name: string;
  routeNumber: string;
  vehicle: string;
  vehicleModel?: string;
  vehicleYear?: string;
  vehicleColor?: string;
  vehicleNumber?: string;
  driverProfileImage?: string;
  driver: string;
  driverDetail: any;
  fleetDetail: any;
  miles: string;
  status: string;
  stops: number;
  completedStops: number;
  membersCount: number;
  membersOnboardCount: number;
  nextStopEta?: string;
  nextStopDelayMinutes: number;
  checked: boolean;
  scheduledStartTime: string;
  scheduledEndTime: string;
  events: TimelineEvent[];
  isLate?: boolean;
  mapStops: MapStop[];
  strokeColor: string;
  vehicleLocation?: any;
  vehicleIconUrl?: any;
  osrmPath?: any[];
  osrmSegments?: Array<{ path: any[]; completed: boolean }>;
  startPct?: number;
  endPct?: number;
  passedWidthPct?: number;
  upcomingWidthPct?: number;
  upcomingLeftPct?: number;
  upcomingStartTime?: string;
}


@Component({
  selector: 'app-route-monitoring',
  templateUrl: './route-monitoring.component.html',
  styleUrls: ['./route-monitoring.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RouteMonitoringComponent implements OnInit, OnDestroy {
  @ViewChild('timelineScrollContainer', { static: false }) timelineScrollContainer!: ElementRef;

  todayDate: string = moment().toISOString();
  user: any;
  timeZone: string = moment.tz.guess();

  routes: Route[] = [];
  headerIntervals: string[] = [];
  gridCols144 = new Array(144).fill(0);

  // Resizer & Sidebar State
  timelineHeight = 400;
  minTimelineHeight = 300;
  isDragging = false;
  isSidebarCollapsed = false;
  startY = 0;
  startHeight = 0;
  currentTimePosition = 0; // The left position in px for the time bar

  // Map State
  latitude = 41.2583;
  longitude = -96.0108;
  bounds: any;
  mapTypeControlOptions = {
    mapTypeIds: ['roadmap', 'satellite'],
    position: 3
  };
  mapStyles: any[] = [
    { "featureType": "all", "elementType": "labels.text.fill", "stylers": [{ "color": "#4b5563" }] },
    { "featureType": "administrative", "elementType": "labels.text.fill", "stylers": [{ "color": "#374151" }] },
    { "featureType": "landscape", "elementType": "all", "stylers": [{ "color": "#f8fafc" }] },
    { "featureType": "road", "elementType": "all", "stylers": [{ "saturation": -100 }, { "lightness": 45 }] },
    { "featureType": "water", "elementType": "all", "stylers": [{ "color": "#e2e8f0" }] }
  ];

  // Modals
  isRouteModalOpen = false;
  isAssignTripModalOpen = false;
  showDetailsModal = false;
  editRouteId: string | null = null;
  selectedDetailsRouteId: string | null = null;
  isLoading = false;
  cancelRouteModalObj: any = null;
  search = '';
  statusFilter = '';

  // Data Caches
  private preDriversMap: any = {};
  private preFleetsMap: any = {};
  private driversLocationMap: any = {};
  private refreshSubscription!: Subscription;
  private hasAppliedInitialBounds = false;
  private simulatedNow = moment().toISOString();
  private hasAutoScrolled = false;

  constructor(
    private sharedDataService: SharedDataService,
    private routeService: RouteService,
    private driverService: DriversService,
    private fleetService: FleetService,
    private socketService: SocketService,
    private cd: ChangeDetectorRef,
    private assignTripService: AssigTripsService
  ) { }

  ngOnInit() {
    this.generateHeaderIntervals();
    this.getUser();
    this.startAutoRefresh();
  }

  ngOnDestroy() {
    if (this.refreshSubscription) this.refreshSubscription.unsubscribe();
  }

  private generateHeaderIntervals() {
    this.headerIntervals = [];
    for (let h = 0; h < 24; h++) {
      const hr = h.toString().padStart(2, '0');
      for (let m = 0; m < 60; m += 10) {
        this.headerIntervals.push(`${hr}:${m.toString().padStart(2, '0')}`);
      }
    }
  }

  private getUser() {
    this.sharedDataService.getUser().pipe(takeUntil(componentDestroyed(this))).subscribe(user => {
      if (user) {
        this.user = user;
        this.timeZone = user.timeZone || moment.tz.guess();
        this.loadInitialData();
      }
    });
  }

  private loadInitialData() {
    // Load drivers and fleets for mapping (Handling direct array responses)
    this.driverService.getDrivers(this.user._id, 'active').pipe(takeUntil(componentDestroyed(this))).subscribe(res => {
      const drivers = Array.isArray(res) ? res : (res && res.data ? res.data : []);
      drivers.forEach(d => this.preDriversMap[d._id] = d);

      this.fleetService.getFleetList({ company: this.user._id }).pipe(takeUntil(componentDestroyed(this))).subscribe(fRes => {
        const fleets = Array.isArray(fRes) ? fRes : (fRes && fRes.data ? fRes.data : []);
        fleets.forEach(f => this.preFleetsMap[f._id] = f);
        this.getRoutes();
        if (!this.hasAutoScrolled) {
          setTimeout(() => {
            this.scrollToCurrentTime();
            this.hasAutoScrolled = true;
          }, 1500);
        }
      });
    });
  }

  private startAutoRefresh() {
    this.updateCurrentTimePosition();
    this.refreshSubscription = interval(30000).pipe(takeUntil(componentDestroyed(this))).subscribe(() => {
      this.updateCurrentTimePosition();
      this.getRoutes(true);
    });
  }

  getRoutes(isRefresh = false) {
    if (!isRefresh) this.isLoading = true;
    const query = { date: moment(this.todayDate).startOf('day').toISOString() };
    this.routeService.getRoutes(query).pipe(
      finalize(() => {
        this.isLoading = false;
        this.cd.markForCheck();
      })
    ).subscribe(res => {
      const rawData = (res && res.data) ? res.data : (Array.isArray(res) ? res : []);

      if (rawData.length > 0) {
        const nowIso = moment().toISOString();
        // Map data and preserve 'checked' state
        const updatedRoutes = rawData.map(dbRoute => {
          const uiRoute = this.mapDatabaseRouteToUI(dbRoute, nowIso);
          const existing = this.routes.find(r => (r.id || r._id) === (uiRoute.id || uiRoute._id));
          if (existing) {
            uiRoute.checked = existing.checked;
            // Preserve GIS road-following segments so lines don't revert on refresh
            if (existing.osrmSegments && existing.osrmSegments.length > 0) {
              uiRoute.osrmSegments = existing.osrmSegments;
            }
          }
          return uiRoute;
        });

        this.routes = updatedRoutes;
        this.cd.markForCheck();

        if (!isRefresh || !this.hasAppliedInitialBounds) {
          this.updateMapBounds();
          this.hasAppliedInitialBounds = true;
        }

      } else {
        this.routes = [];
      }
    }, err => {
      console.error('Error fetching routes:', err);
      this.routes = [];
    });
  }

  mapDatabaseRouteToUI(dbRoute: any, nowIso: string): Route {
    const uiEvents: TimelineEvent[] = [];
    const mapStops: MapStop[] = [];

    // Resolve Driver Name & Details — prefer populated object from API
    const driverRaw = dbRoute.driver;
    const driverId = (driverRaw && typeof driverRaw === 'object') ? driverRaw._id : driverRaw;
    const driverData = (driverRaw && typeof driverRaw === 'object' && driverRaw.displayName)
      ? driverRaw
      : (this.preDriversMap[driverId] || null);
    const driverName = (driverData && driverData.displayName) ? this.toTitleCase(driverData.displayName) : (driverId || 'Unassigned');
    const driverProfileImage = (driverData && (driverData.profileImageURL || driverData.profileImage || driverData.photo || driverData.picture)) || null;

    // Resolve Fleet / Vehicle Details
    // Priority: (1) route.fleet populated obj → (2) preFleetsMap[route fleetId] → (3) preFleetsMap[driver.fleet._id]
    const fleetRaw = dbRoute.fleet || dbRoute.vehicle;
    const fleetId = (fleetRaw && typeof fleetRaw === 'object') ? fleetRaw._id : fleetRaw;

    // Also extract fleet ID from the driver's embedded fleet object as a fallback
    const driverFleetId = (driverData && driverData.fleet && driverData.fleet._id) ? driverData.fleet._id : null;

    // Resolve full fleet data: prefer populated route fleet object, then map lookups
    let fleetData = (fleetRaw && typeof fleetRaw === 'object' && (fleetRaw.setiDecall || fleetRaw.make))
      ? fleetRaw
      : (this.preFleetsMap[fleetId] || this.preFleetsMap[driverFleetId] || null);

    // If still no full fleet data, use the driver's embedded fleet object for basic rig info
    const driverFleetEmbed = (driverData && driverData.fleet) ? driverData.fleet : null;

    const rigNumber = (fleetData && fleetData.setiDecall) || (driverFleetEmbed && driverFleetEmbed.setiDecall) || dbRoute.rigNumber || 'Assign Vehicle';
    const vehicleModel = (fleetData && fleetData.driverCarModel) || (fleetData && fleetData.model) || '';
    const vehicleYear = (fleetData && fleetData.driverCarYear) || (fleetData && fleetData.year) || '';
    const vehicleColor = (fleetData && fleetData.driverCarColor) || '';
    const vehicleNumber = (fleetData && fleetData.driverCarNumber) || (driverFleetEmbed && driverFleetEmbed.driverCarNumber) || '';

    const rawStops = dbRoute.stops || [];
    let rippleDelay = 0;

    // Simple Lateness Check for the whole route
    let isRouteLate = false;
    if (dbRoute.status === 'inprogress') {
      const now = moment();
      const firstPending = rawStops.find(s => s.status === 'pending');
      if (firstPending && moment(firstPending.scheduledArrivalTime).isBefore(now.subtract(5, 'minutes'))) {
        isRouteLate = true;
      }
    }

    const vTimeSource = dbRoute.locationTime || dbRoute.lastLocationUpdate || (dbRoute.status === 'inprogress' ? nowIso : null);
    const vehicleTime = vTimeSource ? this.extractTime(vTimeSource) : null;
    const vehicleUpdateRaw = vTimeSource;

    rawStops.forEach((stop: any, index: number) => {
      if (!stop) return;
      let type: 'home' | 'stop' | 'end' = 'stop';
      if (stop.stopType === 'start') type = 'home';
      else if (stop.stopType === 'end') type = 'end';

      const label = type === 'home' ? 'START' : (type === 'end' ? 'END' : index.toString().padStart(2, '0'));
      const sTime = this.extractTime(stop.scheduledArrivalTime || dbRoute.scheduledStartTime);
      const startPct = this.getTimePct(sTime);

      // Pre-calculate Lateness for Timeline
      const isEventLate = !!vehicleTime && (stop.status === 'pending') && sTime < vehicleTime;
      const lateWidthPct = isEventLate ? this.getDurationPct(sTime, vehicleTime!) : 0;

      const lat = stop.latitude != null ? Number(stop.latitude) : 0;
      const lng = stop.longitude != null ? Number(stop.longitude) : 0;

      const mappedStop: MapStop = {
        lat, lng, label, type,
        status: stop.status || 'pending',
        address: stop.address || 'Unknown Address',
        memberName: stop.name || 'Unknown',
        scheduledTime: sTime,
        eta: stop.eta ? this.extractTime(stop.eta) : (stop.estimatedArrivalTime ? this.extractTime(stop.estimatedArrivalTime) : undefined),
        delayMinutes: stop.delayMinutes || stop.delay || 0,
        iconUrl: this.getMarkerIconUrl({ label, status: stop.status }, dbRoute.status),
        actualOnboardCount: stop.onboardCount || 0,
        actualOffboardCount: stop.offboardCount || 0,
        actualArrival: (stop.actualArrival || stop.arrivalTime || stop.actualArrivalTime) ? this.extractTime(stop.actualArrival || stop.arrivalTime || stop.actualArrivalTime) : undefined,
        actualDeparture: (stop.actualDeparture || stop.actualDepartureTime || stop.departureTime) ? this.extractTime(stop.actualDeparture || stop.actualDepartureTime || stop.departureTime) : undefined,
        plannedOnboard: stop.plannedOnboard || [],
        plannedOffboard: stop.plannedOffboard || [],
        occupancyAfter: stop.occupancyAfter || 0,
        reserveCount: (stop.members && stop.members.length) || 0,
        distanceFromPrev: stop.distanceFromPrev != null ? Number(stop.distanceFromPrev) : undefined,
        durationFromPrev: stop.durationFromPrev != null ? Number(stop.durationFromPrev) : undefined,
      };

      if (lat !== 0 || lng !== 0) mapStops.push(mappedStop);

      uiEvents.push({
        startTime: sTime,
        endTime: sTime,
        label, type,
        status: stop.status || 'pending',
        memberName: stop.name || 'Unknown',
        leftPct: startPct,
        isLate: isEventLate,
        lateWidthPct: lateWidthPct,
        actualOnboard: stop.onboardCount || 0,
        actualOffboard: stop.offboardCount || 0,
        actualArrival: (stop.actualArrival || stop.arrivalTime || stop.actualArrivalTime) ? this.extractTime(stop.actualArrival || stop.arrivalTime || stop.actualArrivalTime) : undefined,
        actualDeparture: (stop.actualDeparture || stop.actualDepartureTime || stop.departureTime) ? this.extractTime(stop.actualDeparture || stop.actualDepartureTime || stop.departureTime) : undefined,
        plannedOnboard: (stop.plannedOnboard && stop.plannedOnboard.length) || 0,
        plannedOffboard: (stop.plannedOffboard && stop.plannedOffboard.length) || 0,
        occupancyAfter: stop.occupancyAfter || 0,
        reserveCount: (stop.members && stop.members.length) || 0,
        distanceFromPrev: stop.distanceFromPrev != null ? Number(stop.distanceFromPrev) : undefined,
        durationFromPrev: stop.durationFromPrev != null ? Number(stop.durationFromPrev) : undefined,
        eta: stop.eta ? this.extractTime(stop.eta) : (stop.estimatedArrivalTime ? this.extractTime(stop.estimatedArrivalTime) : undefined),
        delayMinutes: stop.delayMinutes || stop.delay || 0,
      });
    });

    // Handle Vehicle Location
    let vehicleLocation = null;
    if (dbRoute.latitude && dbRoute.longitude) {
      vehicleLocation = { lat: dbRoute.latitude, lng: dbRoute.longitude };
    } else if (driverData && driverData.latitude && driverData.longitude) {
      vehicleLocation = { lat: driverData.latitude, lng: driverData.longitude };
    }

    // Calculate Vehicle Position for Timeline
    let vehiclePct = 0;
    if (dbRoute.status === 'inprogress' && vehicleTime) {
      vehiclePct = this.getTimePct(vehicleTime);
      uiEvents.push({ startTime: vehicleTime, endTime: vehicleTime, label: 'BUS', type: 'vehicle', leftPct: vehiclePct });
    }

    const sStartTime = this.extractTime(dbRoute.scheduledStartTime);
    const sEndTime = this.extractTime(dbRoute.scheduledEndTime);
    const routeStartPct = this.getTimePct(sStartTime);
    const routeEndPct = this.getTimePct(sEndTime);

    // Progress Line Widths
    const currentRefTime = vehicleTime || sStartTime;
    const passedWidthPct = this.getDurationPct(sStartTime, currentRefTime);

    // Upcoming segment starts from the first pending stop or vehicle time
    const firstPending = rawStops.find(s => s.status === 'pending');
    const upcomingStartTime = firstPending ? this.extractTime(firstPending.scheduledArrivalTime) : null;
    const upcomingWidthPct = upcomingStartTime ? this.getDurationPct(upcomingStartTime, sEndTime) : 0;
    const upcomingLeftPct = upcomingStartTime ? this.getTimePct(upcomingStartTime) : 0;

    const nextEvt = uiEvents.find(e => e.status === 'arrived' || e.status === 'pending');
    const nextStopEtaValue = nextEvt ? nextEvt.startTime : '';

    const existing = this.routes.find(r => (r.id || r._id) === dbRoute._id);

    return {
      id: dbRoute._id,
      _id: dbRoute._id,
      name: this.toTitleCase(dbRoute.routeName || dbRoute.routeCode || 'Unnamed Route'),
      routeNumber: dbRoute.routeCode || dbRoute.routeNumber || 'NA',
      driver: driverName,
      driverDetail: driverData,
      vehicle: rigNumber,
      vehicleModel,
      vehicleYear,
      vehicleColor,
      vehicleNumber,
      driverProfileImage,
      fleetDetail: fleetData,
      miles: dbRoute.totalDistance || 0,
      status: this.toTitleCase(dbRoute.status === 'inprogress' ? 'Active' : dbRoute.status),
      stops: rawStops.length,
      completedStops: rawStops.filter((s: any) => s.status === 'completed' || s.status === 'departed').length,
      membersCount: (dbRoute.reservedAssigns || []).length,
      membersOnboardCount: dbRoute.totalOnboard,
      nextStopEta: nextStopEtaValue,
      nextStopDelayMinutes: nextEvt ? nextEvt.delayMinutes : 0,
      checked: existing ? existing.checked : false,
      scheduledStartTime: sStartTime,
      scheduledEndTime: sEndTime,
      startPct: routeStartPct,
      endPct: routeEndPct,
      passedWidthPct,
      upcomingWidthPct,
      upcomingLeftPct,
      upcomingStartTime,
      isLate: isRouteLate,
      events: uiEvents,
      mapStops: mapStops,
      strokeColor: this.getStrokeColor(dbRoute.status, isRouteLate),
      vehicleLocation: vehicleLocation,
      vehicleIconUrl: this.getVehicleMarkerIconUrl(isRouteLate),
      osrmPath: existing ? existing.osrmPath : []
    };
  }

  private extractTime(iso: string): string {
    if (!iso) return '00:00';
    // If already in HH:mm format, return as is to avoid moment deprecation warnings
    if (typeof iso === 'string' && iso.length === 5 && iso.includes(':')) return iso;
    const m = moment(iso);
    return m.isValid() ? m.format('HH:mm') : '00:00';
  }

  private toTitleCase(s: string): string {
    if (!s) return '';
    return s.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }

  private getStrokeColor(status: string, isLate: boolean): string {
    if (status === 'completed') return '#10b981';
    if (status === 'cancelled') return '#f43f5e';
    return isLate ? '#e11d48' : '#6366f1';
  }

  // ─── GIS Road-Following Polylines ──────────────────────────────────────────
  private async fetchOsrmPathsForRoutes(routes: Route[]) {
    for (const route of routes) {
      if (!route.mapStops || route.mapStops.length < 2) continue;

      // Skip if we already have segments (avoid re-fetching on refresh)
      if (route.osrmSegments && route.osrmSegments.length > 0) continue;

      const segments: Array<{ path: any[]; completed: boolean }> = [];

      for (let i = 0; i < route.mapStops.length - 1; i++) {
        const from = route.mapStops[i];
        const to = route.mapStops[i + 1];

        if (!from.lat || !from.lng || !to.lat || !to.lng) {
          segments.push({ path: [{ lat: from.lat, lng: from.lng }, { lat: to.lat, lng: to.lng }], completed: false });
          continue;
        }

        try {
          const res: any = await this.assignTripService.getRoutingDistance(
            from.lat, from.lng, to.lat, to.lng
          ).toPromise();

          const geometry = res && res.features && res.features[0] && res.features[0].geometry;
          let path: any[] = [];

          if (geometry && geometry.coordinates) {
            path = geometry.coordinates.map((coord: any) => ({ lat: coord[1], lng: coord[0] }));
          } else {
            path = [{ lat: from.lat, lng: from.lng }, { lat: to.lat, lng: to.lng }];
          }

          const isCompleted = from.status === 'completed' || from.status === 'departed';
          segments.push({ path, completed: isCompleted });
        } catch {
          // Fallback: direct straight line segment
          segments.push({
            path: [{ lat: from.lat, lng: from.lng }, { lat: to.lat, lng: to.lng }],
            completed: from.status === 'completed' || from.status === 'departed'
          });
        }
      }

      route.osrmSegments = segments;
      this.cd.markForCheck();
    }
  }

  getMarkerIconUrl(stop: any, routeStatus: string): any {
    const status = stop.status || 'pending';
    let color = '#6366f1'; // indigo
    if (status === 'completed' || status === 'departed') color = '#10b981';
    else if (status === 'skipped') color = '#f43f5e';
    else if (status === 'arrived') color = '#0ea5e9';

    const label = stop.label || '00';
    const isBig = label === 'START' || label === 'END';
    const size = isBig ? 42 : 32;

    const svg = `
      <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
        <circle cx="${size / 2}" cy="${size / 2}" r="${(size / 2) - 3}" fill="white" stroke="${color}" stroke-width="3" />
        <text x="50%" y="50%" dy=".35em" font-family="Inter, system-ui" font-weight="900" font-size="${isBig ? '9' : '11'}" fill="${color}" text-anchor="middle">${label}</text>
      </svg>`;

    return {
      url: 'data:image/svg+xml;base64,' + btoa(svg),
      scaledSize: { width: size, height: size },
      anchor: { x: size / 2, y: size / 2 }
    };
  }

  getVehicleMarkerIconUrl(isLate: boolean): any {
    const color = isLate ? '#f43f5e' : '#6366f1';
    const bg = isLate ? '#fff1f2' : '#eef2ff';
    // Clean NEMT minibus SVG — side profile, clear silhouette
    const svg = `<svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="2" width="44" height="44" rx="10" fill="${bg}" stroke="${color}" stroke-width="2"/>
      <rect x="7" y="16" width="34" height="18" rx="3" fill="${color}"/>
      <rect x="9" y="18" width="10" height="7" rx="1.5" fill="white" opacity="0.85"/>
      <rect x="21" y="18" width="10" height="7" rx="1.5" fill="white" opacity="0.85"/>
      <rect x="33" y="18" width="6" height="7" rx="1.5" fill="white" opacity="0.85"/>
      <circle cx="14" cy="36" r="3.5" fill="white" stroke="${color}" stroke-width="1.5"/>
      <circle cx="34" cy="36" r="3.5" fill="white" stroke="${color}" stroke-width="1.5"/>
      <rect x="7" y="27" width="34" height="2" fill="white" opacity="0.2" rx="1"/>
      <rect x="5" y="20" width="3" height="5" rx="1" fill="#fbbf24"/>
      <rect x="40" y="20" width="3" height="4" rx="1" fill="#f87171"/>
    </svg>`;
    return {
      url: 'data:image/svg+xml;base64,' + btoa(svg),
      scaledSize: { width: 48, height: 48 },
      anchor: { x: 24, y: 24 }
    };
  }

  onRouteToggle(route: Route) {
    route.checked = !route.checked;
    if (route.checked) {
      this.fetchOsrmPathsForRoutes([route]);
    }
    this.updateMapBounds();
  }

  private updateMapBounds() {
    const checked = this.getCheckedRoutes();
    if (checked.length === 0) return;
    try {
      const bounds = new google.maps.LatLngBounds();
      let hasPoints = false;
      checked.forEach(r => {
        if (r.mapStops) {
          r.mapStops.forEach(s => {
            if (s.lat != null && s.lng != null && !isNaN(s.lat) && !isNaN(s.lng)) {
              bounds.extend({ lat: Number(s.lat), lng: Number(s.lng) });
              hasPoints = true;
            }
          });
        }
        if (r.vehicleLocation && r.vehicleLocation.lat != null && r.vehicleLocation.lng != null) {
          bounds.extend({ lat: Number(r.vehicleLocation.lat), lng: Number(r.vehicleLocation.lng) });
          hasPoints = true;
        }
      });
      if (hasPoints) {
        this.bounds = bounds;
      }
    } catch (e) {
      console.error('Error updating map bounds', e);
    }
  }

  get activeRoutesCount(): number { return this.routes.filter(r => r.status === 'Active').length; }
  get totalMembersCount(): number { return this.routes.reduce((acc, r) => acc + (r.membersCount || 0), 0); }
  get totalMembersOnboardCount(): number { return this.routes.reduce((acc, r) => acc + (r.membersOnboardCount || 0), 0); }
  get totalDestinationsCount(): number { return this.routes.reduce((acc, r) => acc + (r.stops || 0), 0); }
  get totalMiles(): number {
    const miles = this.routes.reduce((acc, r) => acc + (parseFloat(r.miles) || 0), 0);
    return Math.round(miles * 10) / 10;
  }
  getCheckedRoutes(): Route[] { return this.routes.filter(r => r.checked); }

  get filteredRoutes(): Route[] {
    if (!this.statusFilter) return this.routes;
    return this.routes.filter(r => r.status === this.statusFilter);
  }

  onStartResize(event: MouseEvent) {
    this.isDragging = true;
    this.startY = event.clientY;
    this.startHeight = this.timelineHeight;
    document.body.style.cursor = 'row-resize';
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (!this.isDragging) return;
    const dy = this.startY - event.clientY;
    this.timelineHeight = Math.max(this.minTimelineHeight, this.startHeight + dy);
  }

  @HostListener('document:mouseup')
  onMouseUp() {
    this.isDragging = false;
    document.body.style.cursor = 'default';
  }

  getTimePct(timeStr: string): number {
    if (!timeStr) return 0;
    const [h, m] = timeStr.split(':').map(Number);
    return ((h * 60 + m) / 1440) * 100;
  }

  getDurationPct(t1: string, t2: string): number {
    if (!t1 || !t2) return 0;
    const [h1, m1] = t1.split(':').map(Number);
    const [h2, m2] = t2.split(':').map(Number);
    const diff = (h2 * 60 + m2) - (h1 * 60 + m1);
    return Math.max(0, (diff / 1440) * 100);
  }

  getVehicleStartTime(route: Route): string | null {
    const v = route.events.find(e => e.type === 'vehicle');
    return v ? v.startTime : null;
  }

  getUpcomingStartTime(route: Route): string | null {
    const vIdx = route.events.findIndex(e => e.type === 'vehicle');
    if (vIdx === -1) return route.scheduledStartTime;
    return route.events[vIdx].startTime;
  }

  isStopLate(route: Route, event: TimelineEvent): boolean {
    const v = this.getVehicleStartTime(route);
    return !!v && event.type !== 'vehicle' && event.status === 'pending' && event.startTime < v;
  }

  openRouteModal(id?: string) {
    this.editRouteId = id || null;
    this.isRouteModalOpen = true;
  }

  closeRouteModal() {
    this.isRouteModalOpen = false;
    this.editRouteId = null;
  }

  onSaveRoute(evt) {
    if (evt) this.getRoutes();
    this.closeRouteModal();
  }

  onAssignTrip(evt) {
    if (evt) this.getRoutes();
    this.isAssignTripModalOpen = false;
  }

  openRouteDetailsModal(id: string) {
    this.selectedDetailsRouteId = id;
    this.showDetailsModal = true;
  }

  closeRouteDetailsModal() {
    this.showDetailsModal = false;
    this.selectedDetailsRouteId = null;
  }

  cancelRoute(route: Route) {
    this.cancelRouteModalObj = route;
  }

  onCancelRoute(evt) {
    this.cancelRouteModalObj = null;
    if (evt) {
      this.getRoutes();
    }
  }

  updateCurrentTimePosition() {
    const now = moment();
    const hour = now.hour();
    const minute = now.minute();
    const colWidth = 80; // match CSS
    const hourWidth = colWidth * 6;
    this.currentTimePosition = (hour * hourWidth) + (minute / 60 * hourWidth);
    this.cd.markForCheck();
  }

  scrollToCurrentTime() {
    this.updateCurrentTimePosition();
    if (this.timelineScrollContainer) {
      const scrollPos = this.currentTimePosition - 400; // Offset to center
      this.timelineScrollContainer.nativeElement.scrollTo({
        left: scrollPos,
        behavior: 'smooth'
      });
    }
  }

  trackByRouteId(index: number, r: Route) { return r.id; }
  trackByEventId(index: number, e: TimelineEvent) { return e.startTime + e.label + e.type; }
}
